import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { isString, min, orderBy, toNumber } from 'lodash-es';
import Icon, { CheckOutlined, CloseOutlined, EnterOutlined } from '@ant-design/icons';
import { ReactComponent as FingerSvg } from 'assets/icons/finger.svg';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';
import { ApplyProduct } from './ApplyProduct';
import {
  useQAPairMeta,
  useQAPairOpenedWithKeepAlive,
  useQAPairVisible,
} from 'hooks/business/im/useQAPair';
import { eventBus } from 'services/eventBus';
import { useMount, useUnmount } from 'ahooks';
import { captureDuration } from 'hooks/business/im/qaPairTimestamp';
import { useQuitQAPair } from './useQuitQAPair';
import { useQAPairPersist } from './useQAPairPersist';
import { messageToAnswerV2 } from './utils';
import { useGetMessageById } from 'hooks/business/message';
import {
  reportSupplementKnowledgeEvent,
  SupplementKnowledgeValueMessage,
} from 'services/api/supplementKnowledge';
import { useSupplementKnowledgeQueue } from 'hooks/business/im/useSupplementKnowledgeQueue';
import { useCleanQAPair } from 'hooks/business/im/useCleanQAPair';
import { logger } from 'services/logger';
import { isIdEqual } from 'utils/is';
import { LocalMessage } from 'hooks/business/conversation';
import {
  useSelectedAnswerMsgIds,
  useSelectedQueryMsgIds,
} from 'hooks/business/im/useMessageSelect';

interface ConfirmModalProps {
  conversation: Conversation.Conversation;
  itemInfo?: Conversation.Product | null;
  selectable: boolean;
  knowledgeQuery: Conversation.KnowledgeQueryMessage;
}

const TIMEOUT = 10 * 1000;

export const ConfirmModal = (props: ConfirmModalProps) => {
  const { conversation, itemInfo, selectable, knowledgeQuery } = props;
  const { storeId, conversationId, buyerNick } = conversation;
  const { t } = useTranslate();
  const qaPairVisible = useQAPairVisible();
  const cleanQAPair = useCleanQAPair();
  const [skuSelected, setSKUSelected] = useState<string[]>([]);
  const qaPairOpenedWithKeepAlive = useQAPairOpenedWithKeepAlive();
  const qaPairMeta = useQAPairMeta();
  const countdownRef = useRef<NodeJS.Timeout | undefined>();

  const getMessageById = useGetMessageById();

  const selectedQueryMsgIds = useSelectedQueryMsgIds('', true);
  const selectedAnswerMsgIds = useSelectedAnswerMsgIds('', true);

  const [loading, setLoading] = useState(false);

  const { submit } = useSupplementKnowledgeQueue();

  const manualQuit = useQuitQAPair();
  const qaPairPersist = useQAPairPersist();

  const isAiErrorReply = qaPairMeta.source === 'AI_ERROR_REPLY';
  const isAutoClose = qaPairMeta.autoClose;
  const isActiveShow = qaPairMeta.isActiveShow;

  /**
   * 用户选择不提交答案
   */
  const userTriggerNotSubmit = () => {
    clearCountdown();
    cleanQAPair();
    manualQuit({
      conversation,
      extra: qaPairOpenedWithKeepAlive
        ? {
            persist: true,
          }
        : undefined,
    });
    qaPairPersist(conversationId);
  };

  const handleClose = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    userTriggerNotSubmit();
  };

  const handleCreateSupplementKnowledge = async () => {
    if (loading) return;

    const { selectMode } = qaPairMeta;

    let queryMsgIds: string[] = [];
    if (selectMode === 'multiple' && selectedQueryMsgIds) {
      queryMsgIds = Array.from(selectedQueryMsgIds.keys());
    } else if (selectMode === 'single' && knowledgeQuery?.msgId) {
      queryMsgIds = [knowledgeQuery.msgId];
    }

    if (!queryMsgIds || queryMsgIds.length === 0) {
      message.warning(t('请选择问题'));
      return;
    }

    logger.info(
      `[ConfirmModal]createSupplementKnowledge conversationId:${conversationId} queryMsgIds:${queryMsgIds.join(
        ',',
      )}`,
    );
    const answerMsgIds = Array.from(selectedAnswerMsgIds || []);
    if (!answerMsgIds || answerMsgIds.length === 0) {
      message.warning(t('请选择答案'));
      return;
    }
    logger.info(
      `[ConfirmModal]createSupplementKnowledge conversationId:${conversationId} answerMsgIds:${answerMsgIds.join(
        ',',
      )}`,
    );

    const minQuestionMsgId = min(queryMsgIds);
    const filteredAnswerMsgIds = answerMsgIds.filter((item) =>
      minQuestionMsgId ? item > minQuestionMsgId : true,
    );

    if (filteredAnswerMsgIds.length === 0) {
      message.warning(t('请选择回答所选买家问题的回复内容'));
      return;
    }

    const sorted = (msgIds: string[]) => {
      const list: LocalMessage[] = msgIds
        .map((item) => getMessageById(item))
        .filter(
          (item) => !!item && isIdEqual(item.conversationId, conversationId),
        ) as LocalMessage[];
      return orderBy(
        list,
        [
          (item) => {
            return isString(item.sendTime) ? toNumber(item.sendTime) : item.sendTime;
          },
        ],
        ['asc'],
      );
    };
    // 1. 根据 sendTime 从小到大排序
    const sortedQueryMessages = sorted(queryMsgIds);
    logger.info(`[ConfirmModal]sortedQueryMessages:${JSON.stringify(sortedQueryMessages)}`);
    const sortedAnswerMessages = sorted(filteredAnswerMsgIds);

    // 构建 Q 和 AI 回复关联
    const botQaMap: Record<string, SupplementKnowledgeValueMessage[]> = {};
    if (selectedQueryMsgIds) {
      selectedQueryMsgIds.forEach((value, key) => {
        const list = sorted(Array.from(value));
        const aiReply = messageToAnswerV2(list);
        if (aiReply.length > 0) {
          botQaMap[key] = aiReply;
        }
      });
    }
    // 构建 answers 数组
    logger.info(
      `[ConfirmModal]createSupplementKnowledge conversationId:${conversationId} sortedAnswerMessages:${sortedAnswerMessages.join(
        ',',
      )}`,
    );
    const originAnswers = messageToAnswerV2(sortedAnswerMessages);
    const originQuestions = messageToAnswerV2(sortedQueryMessages).map((item) => ({
      ...item,
      isBotReply: item.messageId ? botQaMap[item.messageId]?.length > 0 : false,
    }));

    try {
      setLoading(true);
      cleanQAPair();
      submit({
        conversationId,
        spuId: itemInfo?.externalItemId || '',
        skuIds: skuSelected,
        storeId,
        supplementalKnowledgeValue: {
          buyerNick: buyerNick || 'unknown',
          originAnswers,
          originQuestions,
          botQaMap,
        },
      });
      if (isAiErrorReply) {
        message.success(t('感谢您反馈的AI回复有误问题'));
      } else {
        message.success(t('感谢您为知识库补充了一条知识'));
      }
    } catch (err) {
      message.error(t('translation.操作失败，请稍后再试'));
    } finally {
      setLoading(false);
    }
  };

  const clearCountdown = () => {
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = undefined;
    }
  };

  useKeyboardHandler(
    ['Escape'],
    () => {
      userTriggerNotSubmit();
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.MODAL,
      context: () => qaPairVisible,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  useKeyboardHandler(
    ['ctrl+Enter'],
    () => {
      handleCreateSupplementKnowledge();
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.MODAL,
      context: () => qaPairVisible,
      preventDefault: true,
      stopPropagation: true,
    },
  );

  useEffect(() => {
    const handler = () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = undefined;
      }
    };
    eventBus.on('CLEAR_QAPAIR_COUNTDOWN_TIMER', handler);
    return () => {
      eventBus.off('CLEAR_QAPAIR_COUNTDOWN_TIMER', handler);
    };
  }, []);

  useMount(() => {
    if (!qaPairVisible) {
      return;
    }

    // 如果 keepAlive 为 true，不执行倒计时隐藏
    if (qaPairOpenedWithKeepAlive || isAiErrorReply || !isAutoClose) {
      return;
    }

    clearCountdown();

    countdownRef.current = setTimeout(() => {
      cleanQAPair();
      manualQuit({ conversation });
    }, TIMEOUT);
  });

  useUnmount(() => {
    clearCountdown();
  });

  useLayoutEffect(() => {
    captureDuration(conversationId, '$qaPairShowModalDuration');
    if (isActiveShow) {
      reportSupplementKnowledgeEvent({
        eventCode: 'AI_LEARNING_PROMPT_QUESTION',
        questionMessageId: knowledgeQuery.msgId,
        storeId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-w-[250px] bg-[#FFF] rounded-[4px] p-[8px] pointer-events-auto">
      <div className="flex items-center justify-end text-[10px] text-[#A7A7A7] mb-1">
        Esc{t('marketing.关闭')}/Control+
        <EnterOutlined />
        {t('enterprise.提交')}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[#336fff] flex items-center gap-1">
          <Icon component={FingerSvg} style={{ transform: 'rotate(180deg)' }} />
          <span>{t('提交该答案，后续此类商品问题AI将自动回复')}</span>
        </div>
        <div className="flex items-center gap-[10px]">
          <span
            className="bg-[#F1F2F5] rounded-[2px] w-[18px] h-[18px] flex items-center justify-center text-[#777777] cursor-pointer hover:opacity-80"
            onClick={handleClose}
          >
            <CloseOutlined />
          </span>
          <span
            className="bg-[#F1F2F5] rounded-[2px] w-[18px] h-[18px] flex items-center justify-center text-[#336fff] cursor-pointer hover:opacity-80"
            onClick={handleCreateSupplementKnowledge}
          >
            <CheckOutlined />
          </span>
        </div>
      </div>
      {itemInfo && (
        <ApplyProduct
          selectable={selectable}
          imageUrl={itemInfo.imageUrl?.[0]}
          skuInfo={itemInfo.skuInfo}
          skuSelected={skuSelected}
          onSKUChange={setSKUSelected}
          onClick={() => {
            countdownRef.current && clearTimeout(countdownRef.current);
          }}
        />
      )}
    </div>
  );
};
